import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Languages } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useT } from "@/lib/i18n";

function LangSwitch({ inline = false }) {
  const { lang, setLang } = useCart();
  return (
    <div className={`flex items-center gap-1 border border-[#005fca]/40 ${inline ? "self-start" : ""}`} data-testid="lang-switcher">
      <Languages className="w-3.5 h-3.5 text-[#e77300] ml-2" />
      {["en", "pt"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-switch-${l}`}
          className={`px-2 py-1 font-mono-alt text-[10px] tracking-widest uppercase ${lang === l ? "bg-[#e77300] text-white" : "text-white/70 hover:text-[#e77300]"}`}
        >
          {l === "en" ? "EN" : "PT"}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { totalItems, setDrawerOpen } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const t = useT();

  const links = [
    { to: "/", label: t.nav_home, key: "home" },
    { to: "/about", label: t.nav_about, key: "about" },
    { to: "/sponsorship", label: t.nav_shop, key: "shop" },
    { to: "/contact", label: t.nav_contact, key: "contact" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#020815]/85 backdrop-blur-xl border-b border-[#022a67]" data-testid="site-navbar">
      <div className="container-ar flex items-center justify-between h-20 gap-3">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <img src="/images/misc/alight-logo.jpg" alt="Alight Racing" className="h-10 w-auto object-contain" style={{ filter: "brightness(1.05)" }} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-display uppercase tracking-widest transition-colors ${isActive ? "text-[#e77300]" : "text-white/80 hover:text-[#e77300]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:block"><LangSwitch /></div>

          <button onClick={() => setDrawerOpen(true)} className="relative p-2 text-white hover:text-[#e77300] transition-colors" data-testid="nav-cart-button" aria-label="Cart">
            <ShoppingBag className="w-5 h-5" />
            {totalItems > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-[#e77300] text-white rounded-full flex items-center justify-center">{totalItems}</span>
            )}
          </button>

          <button onClick={() => navigate("/sponsorship")} className="hidden xl:inline-flex btn-racing primary" data-testid="nav-become-sponsor-btn">
            <span>{t.nav_become_sponsor}</span>
          </button>

          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#022a67] bg-[#020815]/95" data-testid="mobile-menu">
          <div className="container-ar py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `py-3 font-display uppercase tracking-widest text-sm ${isActive ? "text-[#e77300]" : "text-white/80"}`}
                data-testid={`mnav-${l.key}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-[#022a67] mt-2" data-testid="mobile-lang-wrap">
              <LangSwitch inline />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
